import { useCallback, useEffect, useRef, useState } from 'react';
import { procurementService } from '../services/api';
import {
  acompanharGravacao, envioRecusadoPeloPortal, jobConcluido, jobTerminou, marcasDoPedido, podeGravarNaTela, propostaEnviadaPeloHub,
} from '../utils/gravacaoNoPortal';
import type { JobDeGravacao, Preenchimento, PreviaDaGravacao, RecusaAntesDeGravar } from '../utils/gravacaoNoPortal';

/**
 * O estado da GRAVAÇÃO DE ITENS NO PORTAL de uma proposta (ver utils/gravacaoNoPortal). Fica no workspace, e não no
 * componente da etapa, porque o botão do TOPO e o da etapa Envio são a mesma ação e leem o mesmo estado.
 *
 * - a prévia vem do backend (o mesmo montador da gravação) e traz `ultimaGravacao`: recarregar a página não perde o
 *   resultado; um job ainda rodando volta a ser acompanhado;
 * - o que o Heitor completa (marca/modelo que faltam no cadastro) vai no pedido, em `marcas`, por `codItem`, e é SALVO
 *   no item da proposta ao sair do campo (`salvar`): sair da tela e voltar não perde o digitado (395, 19/10). Só o que
 *   ele digitou: nada de completar o modelo do item com o da proposta;
 * - recusa ANTES de gravar (400 do POST) e falha do job com código viram `recusa`, com o código e os itens;
 * - proposta que ainda não existe no portal (`previa.exigeConfirmacao`): gravar é CADASTRAR e ENVIAR. O mesmo `gravar`
 *   (botão do topo e da etapa) pede a confirmação explícita antes e manda `confirmarEnvio: true`; sem quem confirme, não
 *   envia. Com o sim do PORTAL, a proposta virou enviada no backend e `aoEnviar` relê a lista.
 */
export interface GravacaoNoPortal {
  previa: PreviaDaGravacao | null;
  carregando: boolean;
  erroDaPrevia: string | null;
  preenchido: Preenchimento;
  completar: (codItem: string, campo: 'marca' | 'modelo', valor: string) => void;
  /** Ao sair do campo: grava no cadastro do item o que está na tela, se mudou desde o último salvo. */
  salvar: (codItem: string, campo: 'marca' | 'modelo') => void;
  /** O estado do salvamento por campo, para a indicação ao lado dele. */
  salvamento: Record<string, Partial<Record<'marca' | 'modelo', EstadoDoSalvamento>>>;
  job: JobDeGravacao | null;
  gravando: boolean;
  recusa: RecusaAntesDeGravar | null;
  /** Falha que não é recusa do portal (rede, job perdido): a frase, como veio. */
  erro: string | null;
  podeGravar: boolean;
  gravar: () => Promise<void>;
  recarregar: () => Promise<void>;
}

export type EstadoDoSalvamento = { estado: 'salvando' } | { estado: 'salvo' } | { estado: 'erro'; erro: string };

/** Grava marca/modelo no item da proposta; quem passa é o workspace (o mesmo PUT da montagem). */
/** Pergunta ao Heitor antes do envio (o diálogo é do workspace). `false` = não envia. */
export type ConfirmarEnvio = (previa: PreviaDaGravacao) => Promise<boolean>;

export type SalvarNoCadastro = (codItem: number, campo: 'brand' | 'model', valor: string) => Promise<void>;

const recusaDoErro = (e: any): RecusaAntesDeGravar | null => {
  const d = e?.response?.data;
  return d && typeof d.codigo === 'string' ? { codigo: d.codigo, mensagem: d.mensagem ?? d.message ?? null, message: d.message ?? null, itens: Array.isArray(d.itens) ? d.itens : undefined } : null;
};

export function useGravacaoNoPortal(
  proposalId: number | null | undefined,
  ativo: boolean,
  salvarNoCadastro?: SalvarNoCadastro,
  envio?: { confirmar?: ConfirmarEnvio; aoEnviar?: () => void | Promise<void> },
): GravacaoNoPortal {
  const aoEnviarRef = useRef(envio?.aoEnviar);
  aoEnviarRef.current = envio?.aoEnviar;
  const confirmar = envio?.confirmar;
  const [previa, setPrevia] = useState<PreviaDaGravacao | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erroDaPrevia, setErroDaPrevia] = useState<string | null>(null);
  const [preenchido, setPreenchido] = useState<Preenchimento>({});
  const [job, setJob] = useState<JobDeGravacao | null>(null);
  const [gravando, setGravando] = useState(false);
  const [recusa, setRecusa] = useState<RecusaAntesDeGravar | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvamento, setSalvamento] = useState<GravacaoNoPortal['salvamento']>({});
  // O último valor que o cadastro tem, por campo: sair do campo sem mudar nada não grava de novo.
  const salvoRef = useRef<Record<string, Partial<Record<'marca' | 'modelo', string>>>>({});
  const vivo = useRef(true);
  // ⚠️ Liga de novo ao montar: o StrictMode do React monta, desmonta e remonta, e um cleanup que só desliga deixava a
  // tela presa em "Montando a prévia…" (todo setState era ignorado). A prévia local pegou; o teste não roda StrictMode.
  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);

  const acompanhar = useCallback(async (jobId: string) => {
    setGravando(true);
    try {
      const fim = await acompanharGravacao({
        jobId,
        consultar: (id) => procurementService.consultarGravacaoDeItens(id),
        aoConsultar: (j) => { if (vivo.current) setJob(j); },
      });
      if (!vivo.current) return;
      setJob(fim);
      if (propostaEnviadaPeloHub(fim)) void aoEnviarRef.current?.();
      if (fim.status === 'failed' && fim.codigo) setRecusa({ codigo: fim.codigo, mensagem: fim.erro ?? null });
      else if (!jobConcluido(fim)) setErro(fim.erro || (fim.status === 'desconhecido' ? 'O job da gravação não está mais no servidor: recarregue a proposta.' : 'A gravação falhou.'));
    } finally {
      if (vivo.current) setGravando(false);
    }
  }, []);

  const recarregar = useCallback(async () => {
    if (!proposalId) return;
    setCarregando(true);
    setErroDaPrevia(null);
    try {
      const p: PreviaDaGravacao = await procurementService.previaDaGravacaoDeItens(proposalId);
      if (!vivo.current) return;
      setPrevia(p);
      const ultima = p?.ultimaGravacao ?? null;
      // ⚠️ Prévia sem `ultimaGravacao` não apaga um resultado que a tela já tem: a releitura depois de gravar
      // apagava o placar se a persistência atrasasse (o teste pegou). A persistida, quando vem, vence.
      setJob((atual) => ultima ?? atual);
      // A gravação seguia rodando (aba recarregada no meio): volta a acompanhar a mesma.
      if (ultima && !jobTerminou(ultima)) void acompanhar(ultima.jobId);
    } catch (e: any) {
      if (vivo.current) setErroDaPrevia(e?.response?.data?.mensagem || e?.response?.data?.message || e?.message || 'erro ao montar a prévia');
    } finally {
      if (vivo.current) setCarregando(false);
    }
  }, [proposalId, acompanhar]);

  useEffect(() => {
    if (ativo && proposalId) void recarregar();
  }, [ativo, proposalId, recarregar]);

  const completar = useCallback((codItem: string, campo: 'marca' | 'modelo', valor: string) => {
    setPreenchido((p) => ({ ...p, [codItem]: { ...p[codItem], [campo]: valor } }));
  }, []);

  const preenchidoRef = useRef(preenchido);
  preenchidoRef.current = preenchido;
  const salvar = useCallback((codItem: string, campo: 'marca' | 'modelo') => {
    const digitado = preenchidoRef.current[codItem]?.[campo];
    const linha = previa?.linhas.find((l) => String(l.codItem) === codItem);
    if (digitado === undefined || !linha || !salvarNoCadastro) return; // não mexeu: vale o cadastro, nada a gravar
    const valor = digitado.trim();
    const base = salvoRef.current[codItem]?.[campo] ?? String(linha[campo] ?? '').trim();
    if (valor === base) return;
    const marcar = (e: EstadoDoSalvamento) => {
      if (vivo.current) setSalvamento((s) => ({ ...s, [codItem]: { ...s[codItem], [campo]: e } }));
    };
    marcar({ estado: 'salvando' });
    salvarNoCadastro(Number(codItem), campo === 'marca' ? 'brand' : 'model', valor).then(
      () => {
        salvoRef.current = { ...salvoRef.current, [codItem]: { ...salvoRef.current[codItem], [campo]: valor } };
        marcar({ estado: 'salvo' });
      },
      (e: any) => marcar({ estado: 'erro', erro: e?.message || 'erro ao salvar' }),
    );
  }, [previa, salvarNoCadastro]);

  const podeGravar = podeGravarNaTela(previa, preenchido) && !gravando;

  const gravar = useCallback(async () => {
    if (!previa || !proposalId || gravando) return;
    // ⛔ Envio (cadastrar no portal) só com o sim explícito, e sem quem pergunte não há envio.
    const enviar = previa.exigeConfirmacao === true;
    // ⛔ E não repete um envio que o portal recusou ou cujo destino não se sabe: duas propostas no órgão não se desfazem.
    if (enviar && (envioRecusadoPeloPortal(job) || erro)) return;
    if (enviar && !(confirmar && (await confirmar(previa)))) return;
    setRecusa(null);
    setErro(null);
    try {
      const r = await procurementService.gravarItensNoPortal(proposalId, marcasDoPedido(previa, preenchido), enviar ? { confirmarEnvio: true } : undefined);
      await acompanhar(r.jobId);
      // A prévia de novo: ela traz a `ultimaGravacao` persistida e o que o portal passou a ter.
      if (vivo.current) await recarregar();
    } catch (e: any) {
      const rec = recusaDoErro(e);
      if (rec) setRecusa(rec);
      // A prévia estava velha (o portal não tem mais a proposta): relê, e o botão vira "Enviar proposta ao portal".
      if (rec?.codigo === 'CONFIRMACAO_EXIGIDA' && vivo.current) await recarregar();
      else setErro(e?.response?.data?.mensagem || e?.response?.data?.message || e?.message || 'Erro ao gravar no portal.');
    }
  }, [previa, proposalId, gravando, preenchido, acompanhar, recarregar, confirmar, job, erro]);

  return { previa, carregando, erroDaPrevia, preenchido, completar, salvar, salvamento, job, gravando, recusa, erro, podeGravar, gravar, recarregar };
}
